import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Award, GraduationCap, Tv, Newspaper, Heart, Building2, Stethoscope, AlertTriangle, Plus, Trash2, Edit, Upload, Image } from 'lucide-react';
import api from '@/utils/api';

const categories = [
  { id: 'awards', label: 'Awards / Appreciation', icon: Award },
  { id: 'career', label: 'Career Guidelines', icon: GraduationCap },
  { id: 'media', label: 'Media', icon: Tv },
  { id: 'news', label: 'News', icon: Newspaper },
  { id: 'education', label: 'Education & Support', icon: Heart },
  { id: 'csr', label: 'CSR Projects', icon: Building2 },
  { id: 'health', label: 'Health', icon: Stethoscope },
  { id: 'disaster', label: 'Disaster Management', icon: AlertTriangle },
];

const AchievementsCMS = () => {
  const [achievements, setAchievements] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('awards');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [newItem, setNewItem] = useState({ category: 'awards', title: '', description: '', image: '' });
  const [uploading, setUploading] = useState(false);

  useEffect(() => { fetchAchievements(); }, []);

  const fetchAchievements = async () => {
    setLoading(true);
    try {
      const response = await api.get('/cms/achievements');
      setAchievements(response.data.achievements || {});
    } catch (error) {
      toast.error('Failed to fetch achievements');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file, isEdit = false) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('page_type', 'achievements');
      formData.append('image_type', activeTab);
      const response = await api.post('/cms/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const path = response.data.path || '';
      const imageUrl = /^https?:\/\//i.test(path) ? path : `${process.env.REACT_APP_BACKEND_URL}${path}`;
      if (isEdit && editItem) {
        setEditItem({ ...editItem, image: imageUrl });
      } else {
        setNewItem({ ...newItem, image: imageUrl });
      }
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleAdd = async () => {
    if (!newItem.title) { toast.error('Title is required'); return; }
    try {
      const payload = { ...newItem, category: activeTab };
      await api.post('/cms/achievements', payload);
      toast.success('Achievement added');
      setShowAddDialog(false);
      setNewItem({ category: activeTab, title: '', description: '', image: '' });
      fetchAchievements();
    } catch (error) {
      toast.error('Failed to add achievement');
    }
  };

  const handleUpdate = async () => {
    if (!editItem) return;
    try {
      await api.put(`/cms/achievements/${editItem.id}`, editItem);
      toast.success('Achievement updated');
      setShowEditDialog(false);
      setEditItem(null);
      fetchAchievements();
    } catch (error) {
      toast.error('Failed to update achievement');
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Delete this achievement?')) return;
    try {
      await api.delete(`/cms/achievements/${itemId}`);
      toast.success('Achievement deleted');
      fetchAchievements();
    } catch (error) {
      toast.error('Failed to delete achievement');
    }
  };

  const openEdit = (item) => {
    setEditItem({ ...item });
    setShowEditDialog(true);
  };

  const currentItems = achievements[activeTab] || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Achievements CMS</h1>
          <p className="text-slate-600 mt-1">Manage category-wise achievement images and content</p>
        </div>
        <Button data-testid="add-achievement-btn" onClick={() => { setNewItem({ category: activeTab, title: '', description: '', image: '' }); setShowAddDialog(true); }}>
          <Plus className="w-4 h-4 mr-2" /> Add Achievement
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="flex flex-wrap gap-2 mb-6 bg-transparent h-auto">
          {categories.map((cat) => (
            <TabsTrigger
              key={cat.id}
              value={cat.id}
              data-testid={`tab-${cat.id}`}
              className="flex items-center gap-2 px-3 py-2 rounded-full border border-slate-200 data-[state=active]:bg-teal-600 data-[state=active]:text-white data-[state=active]:border-teal-600"
            >
              <cat.icon className="w-4 h-4" />
              <span className="text-sm">{cat.label}</span>
              <span className="text-xs opacity-70">({(achievements[cat.id] || []).length})</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {categories.map((cat) => (
          <TabsContent key={cat.id} value={cat.id}>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
              </div>
            ) : (achievements[cat.id] || []).length === 0 ? (
              <Card className="p-12 text-center border-slate-200">
                <Image className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-600">No achievements in this category yet</p>
                <Button variant="outline" className="mt-4" onClick={() => { setNewItem({ category: cat.id, title: '', description: '', image: '' }); setShowAddDialog(true); }}>
                  <Plus className="w-4 h-4 mr-2" /> Add First Achievement
                </Button>
              </Card>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {(achievements[cat.id] || []).map((item) => (
                  <Card key={item.id} className="overflow-hidden border-slate-200 group relative">
                    <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                      {item.image ? (
                        <img src={item.image} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Image className="w-12 h-12 text-slate-300" />
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-sm font-medium text-slate-700 line-clamp-2">{item.title}</p>
                      {item.description && <p className="text-xs text-slate-500 mt-1 line-clamp-1">{item.description}</p>}
                    </div>
                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button data-testid={`edit-${item.id}`} size="icon" variant="secondary" className="h-7 w-7" onClick={() => openEdit(item)}>
                        <Edit className="w-3 h-3" />
                      </Button>
                      <Button data-testid={`delete-${item.id}`} size="icon" variant="destructive" className="h-7 w-7" onClick={() => handleDelete(item.id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Achievement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
              <Select value={newItem.category || activeTab} onValueChange={(val) => setNewItem({ ...newItem, category: val })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
              <Input data-testid="new-achievement-title" value={newItem.title} onChange={(e) => setNewItem({ ...newItem, title: e.target.value })} placeholder="Achievement title" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Description (Optional)</label>
              <Textarea value={newItem.description} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} placeholder="Brief description..." rows={2} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Image</label>
              {newItem.image && (
                <div className="mb-2 w-32 h-24 rounded overflow-hidden bg-slate-100">
                  <img src={newItem.image} alt="preview" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex gap-2">
                <Input data-testid="new-achievement-image-url" value={newItem.image} onChange={(e) => setNewItem({ ...newItem, image: e.target.value })} placeholder="Image URL or upload below" />
              </div>
              <label className="mt-2 inline-flex items-center gap-2 px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded cursor-pointer text-sm transition-colors">
                <Upload className="w-4 h-4" />
                {uploading ? 'Uploading...' : 'Upload Image'}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e.target.files[0])} disabled={uploading} />
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
              <Button data-testid="save-new-achievement-btn" onClick={handleAdd}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Achievement</DialogTitle>
          </DialogHeader>
          {editItem && (
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                <Select value={editItem.category} onValueChange={(val) => setEditItem({ ...editItem, category: val })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
                <Input data-testid="edit-achievement-title" value={editItem.title} onChange={(e) => setEditItem({ ...editItem, title: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <Textarea value={editItem.description || ''} onChange={(e) => setEditItem({ ...editItem, description: e.target.value })} rows={2} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Image</label>
                {editItem.image && (
                  <div className="mb-2 w-32 h-24 rounded overflow-hidden bg-slate-100">
                    <img src={editItem.image} alt="preview" className="w-full h-full object-cover" />
                  </div>
                )}
                <Input data-testid="edit-achievement-image-url" value={editItem.image || ''} onChange={(e) => setEditItem({ ...editItem, image: e.target.value })} placeholder="Image URL" />
                <label className="mt-2 inline-flex items-center gap-2 px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded cursor-pointer text-sm transition-colors">
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Uploading...' : 'Upload New Image'}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e.target.files[0], true)} disabled={uploading} />
                </label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
                <Button data-testid="save-edit-achievement-btn" onClick={handleUpdate}>Update</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AchievementsCMS;
